import type { SimulationState } from '@/types/api';

/**
 * Maps simulation state to Tailwind color classes for badges.
 */
export const stateBadgeClass = (state: SimulationState): string => {
  switch (state) {
    case 'complete_ok':
      return 'bg-green-100 text-green-800';
    case 'complete_collisions':
      return 'bg-red-100 text-red-800';
    case 'running':
      return 'bg-blue-100 text-blue-800 animate-pulse';
    case 'error':
      return 'bg-red-100 text-red-700';
    case 'obsolete':
      return 'bg-slate-100 text-slate-600';
    case 'ready':
      return 'bg-orange-100 text-orange-800';
    case 'incomplete':
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

/**
 * Maps simulation state to Tailwind classes for banners.
 */
export const stateBannerClass = (state: SimulationState): string => {
  switch (state) {
    case 'complete_ok':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'complete_collisions':
      return 'bg-red-50 text-red-800 border-red-200';
    case 'running':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'error':
      return 'bg-red-50 text-red-800 border-red-200';
    case 'obsolete':
      return 'bg-slate-50 text-slate-500 border-slate-200';
    case 'ready':
      return 'bg-orange-50 text-orange-800 border-orange-200';
    case 'incomplete':
    default:
      return 'bg-slate-50 text-slate-500 border-slate-200';
  }
};

/**
 * Returns whether the given simulation state represents a terminal (non-running) state.
 */
export const isTerminalState = (state: SimulationState): boolean =>
  ['complete_ok', 'complete_collisions', 'error', 'incomplete', 'ready', 'obsolete'].includes(state);

/**
 * Returns whether a simulation is currently running for this project.
 * While running, project files should not be modified.
 */
export const isSimulationRunning = (state: SimulationState): boolean => state === 'running';
